/**
 * The client half of the sync protocol (docs/design/03-sync-protocol.md §3).
 *
 * Just the three calls and their contracts. There is no outbox and no replica
 * yet: the README's Phase 2 deliverable is a client that proves push, pull and
 * bootstrap work end to end, and Phase 3 builds the Dexie stores behind them.
 *
 * What is *not* throwaway is that every response is parsed with the shared
 * schema, so a drift between client and server fails here rather than somewhere
 * downstream, and that pulled clocks are folded into the local one.
 */
import {
  anyEvent,
  bootstrapResponse,
  pullResponse,
  pushResponse,
  type AnyEvent,
  type BootstrapResponse,
  type PullResponse,
  type PushResponse,
} from '@intentionalist/shared';
import { apiFetch } from './api.js';
import { deviceId, observe } from './clock.js';

export async function push(events: readonly AnyEvent[]): Promise<PushResponse> {
  const body = await apiFetch<unknown>('/sync/push', {
    method: 'POST',
    body: JSON.stringify({ deviceId: deviceId(), events }),
  });
  return pushResponse.parse(body);
}

/**
 * One page of the log.
 *
 * Every event's clock is observed before the caller sees it, so a later local
 * write is ordered after everything just received even if this device's wall
 * clock is behind.
 */
export async function pull(since?: string, limit = 500): Promise<PullResponse> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (since !== undefined && since !== '') query.set('since', since);

  const page = pullResponse.parse(await apiFetch<unknown>(`/sync/pull?${query.toString()}`));
  let implausible = 0;
  for (const event of page.events) {
    const parsed = anyEvent.safeParse(event);
    // An event the schema rejects is a server-side problem, not something to
    // apply; it is skipped rather than allowed to abort the whole page.
    if (parsed.success && observe(parsed.data.hlc)) implausible += 1;
  }
  if (implausible > 0) {
    // ADR-0013: not adopted, so this device's clock is unharmed, but the events
    // will keep winning their fields until real time passes them.
    console.warn(
      `[sync] ${implausible} event(s) carry a clock more than a day ahead; ` +
        'a device clock is probably wrong.',
    );
  }
  return page;
}

/** Drains the log from a cursor, following `hasMore` rather than assuming one page. */
export async function pullAll(
  since?: string,
  limit = 500,
): Promise<{
  events: unknown[];
  cursor: string;
}> {
  const events: unknown[] = [];
  let cursor = since ?? '';
  for (;;) {
    const page = await pull(cursor, limit);
    events.push(...page.events);
    cursor = page.nextCursor;
    if (!page.hasMore) return { events, cursor };
  }
}

export async function bootstrap(page?: string, limit = 500): Promise<BootstrapResponse> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (page !== undefined && page !== '') query.set('page', page);
  return bootstrapResponse.parse(await apiFetch<unknown>(`/sync/bootstrap?${query.toString()}`));
}

/**
 * The whole current state, plus the cursor to pull from afterwards.
 *
 * The cursor comes from the first page and is the same on every page by design --
 * see `bootstrapResponse.cursor`. Taking it from the last page would be just as
 * correct but reads as though it mattered, so the first is used deliberately.
 */
export async function bootstrapAll(limit = 500): Promise<{
  records: { kind: string }[];
  cursor: string;
}> {
  const records: { kind: string }[] = [];
  let next: string | undefined;
  let cursor: string | null = null;

  for (;;) {
    const page: BootstrapResponse = await bootstrap(next, limit);
    records.push(...page.records);
    cursor ??= page.cursor;
    if (page.nextPage === undefined) return { records, cursor };
    next = page.nextPage;
  }
}
